import { useCallback, useState } from 'react';

/**
 * Interface representing the result of the useUndoRedo hook.
 *
 * @template T - The type of the state being managed by the undo/redo functionality.
 */
export interface UseUndoRedoResult<T> {
  /** The current value in the undo/redo history. */
  readonly value: T;
  /** Indicates whether an undo action can be performed. */
  readonly canUndo: boolean;
  /** Indicates whether a redo action can be performed. */
  readonly canRedo: boolean;
  /** A function to clear and restart the undo/redo history. */
  readonly reset: (initialValue: T) => void;
  /** A function to set the current value in the undo/redo history. */
  readonly push: (value: T) => void;
  /** A function to undo the last action. */
  readonly undo: () => void;
  /** A function to redo the last undone action. */
  readonly redo: () => void;
}

/**
 * Custom hook to manage an undo/redo history for a given state type.
 *
 * @template T - The type of the state to manage.
 * @param initialValue - The initial value to start the history with.
 * @param limit - The maximum number of states to keep in the history. Defaults to 50.
 * @returns An object containing the current value, functions to push a new state, undo an action, and redo an action, and boolean indicators for whether an undo or redo can be performed.
 *
 * @example
 * ```tsx
 * const Component = () => {
 *   const { value, push, undo, redo, canUndo, canRedo } = useUndoRedo(0);
 *
 *   return (
 *     <div>
 *       <div>Value: {value}</div>
 *       <button onClick={() => push(value + 1)}>Increment</button>
 *       <button onClick={undo} disabled={!canUndo}>Undo</button>
 *       <button onClick={redo} disabled={!canRedo}>Redo</button>
 *     </div>
 *   );
 * };
 * ```
 */
export function useUndoRedo<T>(initialValue: T, limit = 50): UseUndoRedoResult<T> {
  const [history, setHistory] = useState<{ stack: T[]; index: number }>({
    stack: [initialValue],
    index: 0,
  });

  const reset = useCallback((initialValue: T) => {
    setHistory({ stack: [initialValue], index: 0 });
  }, []);

  const push = useCallback(
    (value: T) => {
      setHistory((current) => ({
        stack: [...current.stack.slice(0, current.index + 1), value].slice(-limit - 1),
        index: Math.min(current.index + 1, limit),
      }));
    },
    [limit]
  );

  const undo = useCallback(() => {
    setHistory((current) => ({ stack: current.stack, index: Math.max(0, current.index - 1) }));
  }, []);

  const redo = useCallback(() => {
    setHistory((current) => ({
      stack: current.stack,
      index: Math.min(current.stack.length - 1, current.index + 1),
    }));
  }, []);

  return {
    value: history.stack[history.index],
    canUndo: history.index > 0,
    canRedo: history.index < history.stack.length - 1,
    reset,
    push,
    undo,
    redo,
  } as const;
}
